import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ec2MonitoringService } from '../services/api';
import { mensagemDoErroDeCarga } from '../design';
import { TONE_TEXT } from '../utils/statusColors';
import { chaveDasMetricas } from '../utils/infra';
import {
  AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { fraseDaAusencia, linhasDoMotivo, DiagnosticoDoMonitor } from '../utils/motivoDaMetrica';

interface InstanceOverview {
  key: string;
  name: string;
  instanceId: string | null;
  state: string;
  cpu: number | null;
  memory: number | null;
  disk: number | null;
  networkIn: number | null;
  networkOut: number | null;
  statusCheck: boolean | null;
  error?: string;
  /** Por que memória e disco não têm número (backend #530). Ausente em backend antigo. */
  diagnostico?: DiagnosticoDoMonitor | null;
}

interface OverviewData {
  instances: InstanceOverview[];
  lastUpdated: string;
}

interface MetricsData {
  key: string;
  name: string;
  period: { start: string; end: string; hours: number };
  cpu: Array<{ timestamp: string; average: number; max: number }>;
  memory: Array<{ timestamp: string; average: number }> | null;
  disk: Array<{ timestamp: string; average: number }> | null;
  network: {
    in: Array<{ timestamp: string; bytes: number }>;
    out: Array<{ timestamp: string; bytes: number }>;
  };
  uptime: { percent: number; totalChecks: number; failedChecks: number };
  diagnostico?: DiagnosticoDoMonitor | null;
  lastUpdated: string;
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatTime = (timestamp: string, hours: number): string => {
  const d = new Date(timestamp);
  if (hours <= 24) {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
  }
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' }) + ' ' +
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
};

const getBarColor = (value: number | null): string => {
  if (value === null) return 'bg-slate-600';
  // E1 (Heitor, 23/10/2026): o uso normal é neutro; amarelo e vermelho ficam, porque a máquina carregada pede atenção.
  if (value < 70) return 'bg-slate-500';
  if (value < 90) return 'bg-yellow-500';
  return 'bg-red-500';
};

const getTextColor = (value: number | null): string => {
  if (value === null) return 'text-slate-500';
  if (value < 70) return 'text-central-texto';
  if (value < 90) return TONE_TEXT.warning;
  return TONE_TEXT.danger;
};

const MetricBar = ({ label, value, color }: { label: string; value: number | null; color?: string }) => (
  <div className="flex items-center gap-3">
    <span className="text-slate-400 text-sm w-20">{label}</span>
    <div className="flex-1 h-3 bg-slate-700 rounded-full overflow-hidden">
      {value !== null ? (
        <div
          className={`h-full rounded-full transition-all duration-500 ${color || getBarColor(value)}`}
          style={{ width: `${Math.min(value, 100)}%` }}
        />
      ) : (
        <div className="h-full w-full bg-slate-700 flex items-center justify-center">
          <span className="text-[8px] text-slate-500">N/A</span>
        </div>
      )}
    </div>
    <span className={`text-sm font-mono w-12 text-right ${getTextColor(value)}`}>
      {value !== null ? `${value.toFixed(1)}%` : '—'}
    </span>
  </div>
);

const PERIOD_OPTIONS = [
  { label: '1h', value: 1 },
  { label: '6h', value: 6 },
  { label: '24h', value: 24 },
  { label: '7d', value: 168 },
];

export default function Ec2MonitoringTab() {
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [hours, setHours] = useState(6);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // D5 (11/10/2026): o auto-refresh que falhava depois de uma leitura boa trocava a tela inteira pelo erro; agora os
  // números ficam, com a hora deles e o motivo de não terem sido atualizados.
  const [falhaDaAtualizacao, setFalhaDaAtualizacao] = useState<string | null>(null);
  const temOverviewRef = useRef(false);
  // D5: as métricas valem para UMA instância num período. A resposta carrega a chave; a que chega para uma chave que
  // já não está na tela é descartada (trocar de instância mostrava o gráfico da anterior com o título da nova).
  const [metricsChave, setMetricsChave] = useState<string | null>(null);
  const [metricsErro, setMetricsErro] = useState<{ chave: string; motivo: string } | null>(null);
  const chavePedidaRef = useRef<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchOverview = useCallback(async () => {
    try {
      const data = await ec2MonitoringService.getOverview();
      setOverview(data);
      temOverviewRef.current = true;
      setError(null);
      setFalhaDaAtualizacao(null);
    } catch (err: any) {
      const motivo = err?.response?.data?.message || mensagemDoErroDeCarga(err);
      if (temOverviewRef.current) setFalhaDaAtualizacao(motivo);
      else setError(motivo);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMetrics = useCallback(async (key: string, h: number) => {
    const chave = chaveDasMetricas(key, h);
    chavePedidaRef.current = chave;
    setMetricsLoading(true);
    try {
      const data = await ec2MonitoringService.getMetrics(key, h);
      if (chavePedidaRef.current !== chave) return;
      setMetrics(data);
      setMetricsChave(chave);
      setMetricsErro(null);
    } catch (err: any) {
      if (chavePedidaRef.current !== chave) return;
      setMetricsErro({ chave, motivo: err?.response?.data?.message || mensagemDoErroDeCarga(err) });
    } finally {
      if (chavePedidaRef.current === chave) setMetricsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Auto refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchOverview();
        if (selectedInstance) {
          fetchMetrics(selectedInstance, hours);
        }
      }, 60_000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchOverview, fetchMetrics, selectedInstance, hours]);

  // Fetch metrics when instance or period changes
  useEffect(() => {
    if (selectedInstance) {
      fetchMetrics(selectedInstance, hours);
    }
  }, [selectedInstance, hours, fetchMetrics]);

  const handleViewDetails = (key: string) => {
    if (selectedInstance === key) {
      setSelectedInstance(null);
      setMetrics(null);
      chavePedidaRef.current = null;
    } else {
      setSelectedInstance(key);
    }
  };

  const stateLabel = (state: string) => {
    const labels: Record<string, { text: string; color: string }> = {
      // Operacional é o normal: neutro (23/10/2026). Parado, parando e erro seguem no tom.
      running: { text: 'Operacional', color: 'text-central-texto-fraco' },
      stopped: { text: 'Parado', color: TONE_TEXT.danger },
      stopping: { text: 'Parando...', color: TONE_TEXT.warning },
      pending: { text: 'Iniciando...', color: TONE_TEXT.warning },
      unknown: { text: 'Desconhecido', color: 'text-slate-400' },
      error: { text: 'Erro', color: TONE_TEXT.danger },
    };
    return labels[state] || labels.unknown;
  };

  const customTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-lg">
        <p className="text-slate-400 text-xs mb-2">{label}</p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
            {entry.name !== 'Network In' && entry.name !== 'Network Out' ? '%' : ''}
          </p>
        ))}
      </div>
    );
  };

  const networkTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-lg">
        <p className="text-slate-400 text-xs mb-2">{label}</p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatBytes(entry.value)}
          </p>
        ))}
      </div>
    );
  };

  const chaveAtual = selectedInstance ? chaveDasMetricas(selectedInstance, hours) : null;
  const metricasDaTela = metricsChave !== null && metricsChave === chaveAtual ? metrics : null;
  const erroDasMetricas = metricsErro && metricsErro.chave === chaveAtual ? metricsErro.motivo : null;
  const hora = (iso?: string) => (iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' }) : '');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="bg-red-500/10 border border-red-500/20 rounded-central p-6 text-center">
        <p className="text-red-400">{error}</p>
        <button onClick={fetchOverview} className="mt-3 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 text-sm">
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Monitoramento EC2</h2>
          {overview?.lastUpdated && (
            <p className="text-slate-400 text-sm mt-1">
              Atualizado em {new Date(overview.lastUpdated).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
            </p>
          )}
          {falhaDaAtualizacao && (
            <p role="alert" className={`text-sm mt-1 ${TONE_TEXT.warning}`}>
              Não consegui atualizar ({falhaDaAtualizacao}): os números são de {hora(overview?.lastUpdated) || 'antes'}.
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-slate-400 text-sm">Auto-refresh</span>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`w-10 h-5 rounded-full transition-colors relative ${autoRefresh ? 'bg-primary' : 'bg-slate-600'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${autoRefresh ? 'left-5' : 'left-0.5'}`} />
            </button>
          </label>
          <button
            onClick={() => { fetchOverview(); if (selectedInstance) fetchMetrics(selectedInstance, hours); }}
            className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm font-medium flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Atualizar
          </button>
        </div>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {overview?.instances.map((inst) => {
          const sl = stateLabel(inst.state);
          const isSelected = selectedInstance === inst.key;
          return (
            <div
              key={inst.key}
              className={`bg-central-cartao rounded-central border border-central-borda transition-colors ${isSelected ? 'border-l-[3px] border-l-primary bg-central-elevado' : ''} p-5`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className={`w-3 h-3 rounded-full ${inst.state === 'running' ? 'bg-slate-400' : inst.state === 'stopped' ? 'bg-red-400' : 'bg-slate-500'}`} data-ponto-do-estado={inst.state} />
                  <h3 className="text-white font-semibold text-lg">{inst.name}</h3>
                </div>
                <span className={`text-sm font-medium ${sl.color}`}>{sl.text}</span>
              </div>

              {inst.instanceId ? (
                <div className="space-y-3">
                  <MetricBar label="CPU" value={inst.cpu} />
                  <MetricBar label="Memória" value={inst.memory} />
                  <MetricBar label="Disco" value={inst.disk} />

                  {/* O motivo é o do backend (#530); sem ele, "sem dado", nunca uma causa deduzida (23/10/2026). J1: o mesmo
                      motivo para memória e disco vira uma linha só. */}
                  {linhasDoMotivo(inst.memory === null, inst.disk === null, inst.diagnostico).map((l) => (
                    <p key={l.chave} className="text-xs text-slate-500" data-motivo={l.chave}>
                      {l.rotulo}: {l.frase}
                    </p>
                  ))}

                  <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                    <div className="flex gap-4 text-xs text-slate-400">
                      {inst.networkIn !== null && (
                        <span>In: {formatBytes(inst.networkIn)}</span>
                      )}
                      {inst.networkOut !== null && (
                        <span>Out: {formatBytes(inst.networkOut)}</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleViewDetails(inst.key)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        isSelected
                          ? 'bg-slate-600 text-white font-semibold'
                          : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      }`}
                    >
                      {isSelected ? 'Ocultar' : 'Ver Detalhes'}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-sm">Instance ID não configurado</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Detailed metrics */}
      {selectedInstance && (
        <div className="space-y-4">
          {/* Period selector */}
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold">
              Detalhes — {metricasDaTela?.name || selectedInstance}
              {metricasDaTela?.lastUpdated && (
                <span className="ml-2 text-xs font-normal text-slate-400">atualizado às {hora(metricasDaTela.lastUpdated)}</span>
              )}
            </h3>
            <div className="flex rounded-lg border border-slate-700 overflow-hidden">
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setHours(opt.value)}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${
                    hours === opt.value
                      ? 'bg-slate-600 text-white font-semibold'
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {erroDasMetricas && (
            <div role="alert" className={`text-sm ${TONE_TEXT.danger} flex items-center gap-3`}>
              <span>
                {metricasDaTela
                  ? `Não consegui atualizar as métricas (${erroDasMetricas}): o gráfico é de ${hora(metricasDaTela.lastUpdated) || 'antes'}.`
                  : `Não consegui carregar as métricas desta instância (${erroDasMetricas}).`}
              </span>
              <button onClick={() => selectedInstance && fetchMetrics(selectedInstance, hours)} className="px-3 py-1.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 text-xs">
                Tentar de novo
              </button>
            </div>
          )}
          {metricsLoading && !metricasDaTela ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
            </div>
          ) : metricasDaTela ? (
            <div className="space-y-4">
              {/* Uptime badge */}
              {metricasDaTela.uptime && (
                <div className="bg-central-cartao rounded-central border border-central-borda p-4 flex items-center gap-4">
                  <div className={`text-2xl font-bold ${metricasDaTela.uptime.percent >= 99.9 ? 'text-central-texto' : metricasDaTela.uptime.percent >= 99 ? TONE_TEXT.warning : TONE_TEXT.danger}`}>
                    {metricasDaTela.uptime.percent}%
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Uptime</p>
                    <p className="text-slate-400 text-xs">
                      Últimas {hours <= 24 ? `${hours}h` : `${Math.round(hours / 24)}d`}
                      {metricasDaTela.uptime.failedChecks > 0 && ` — ${metricasDaTela.uptime.failedChecks} falha(s) detectada(s)`}
                    </p>
                  </div>
                </div>
              )}

              {/* Charts grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* CPU Chart */}
                <div className="bg-central-cartao rounded-central border border-central-borda p-5">
                  <h4 className="text-white font-semibold mb-4">CPU (%)</h4>
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={metricasDaTela.cpu.map(d => ({ ...d, time: formatTime(d.timestamp, hours) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="time" tick={{ fill: '#94a3b8', fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <Tooltip content={customTooltip} />
                      <Area type="monotone" dataKey="average" name="CPU Média" stroke="#f97316" fill="#f97316" fillOpacity={0.2} strokeWidth={2} />
                      <Area type="monotone" dataKey="max" name="CPU Máx" stroke="#fb923c" fill="transparent" strokeWidth={1} strokeDasharray="3 3" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Memory Chart */}
                <div className="bg-central-cartao rounded-central border border-central-borda p-5">
                  <h4 className="text-white font-semibold mb-4">Memória (%)</h4>
                  {metricasDaTela.memory && metricasDaTela.memory.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={metricasDaTela.memory.map(d => ({ ...d, time: formatTime(d.timestamp, hours) }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="time" tick={{ fill: '#94a3b8', fontSize: 11 }} interval="preserveStartEnd" />
                        <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <Tooltip content={customTooltip} />
                        <Area type="monotone" dataKey="average" name="Memória" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[240px] px-6 text-center text-slate-500 text-sm" data-motivo="memoria">
                      {fraseDaAusencia(metricasDaTela.diagnostico?.memoria)}
                    </div>
                  )}
                </div>

                {/* Disk Chart */}
                <div className="bg-central-cartao rounded-central border border-central-borda p-5">
                  <h4 className="text-white font-semibold mb-4">Disco (%)</h4>
                  {metricasDaTela.disk && metricasDaTela.disk.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={metricasDaTela.disk.map(d => ({ ...d, time: formatTime(d.timestamp, hours) }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="time" tick={{ fill: '#94a3b8', fontSize: 11 }} interval="preserveStartEnd" />
                        <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <Tooltip content={customTooltip} />
                        <Area type="monotone" dataKey="average" name="Disco" stroke="#a855f7" fill="#a855f7" fillOpacity={0.2} strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[240px] px-6 text-center text-slate-500 text-sm" data-motivo="disco">
                      {fraseDaAusencia(metricasDaTela.diagnostico?.disco)}
                    </div>
                  )}
                </div>

                {/* Network Chart */}
                <div className="bg-central-cartao rounded-central border border-central-borda p-5">
                  <h4 className="text-white font-semibold mb-4">Rede (I/O)</h4>
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={(() => {
                      const netIn = metricasDaTela.network.in;
                      const netOut = metricasDaTela.network.out;
                      const combined = netIn.map((d, i) => ({
                        time: formatTime(d.timestamp, hours),
                        in: d.bytes,
                        out: netOut[i]?.bytes || 0,
                      }));
                      return combined;
                    })()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="time" tick={{ fill: '#94a3b8', fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v: number) => formatBytes(v)} />
                      <Tooltip content={networkTooltip} />
                      <Line type="monotone" dataKey="in" name="Network In" stroke="#4ade80" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="out" name="Network Out" stroke="#f97316" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
